import React from 'react';
import {
  InsertDriveFile as FileIcon,
  AudioFile as AudioIcon,
  VideoFile as VideoIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import { isDAWProject, getFileCategory, getDAWType } from './fileUtils';
import DAWLogo from '../components/_misc/DAWLogo';

/**
 * Gets the appropriate icon component for a file based on its name and content type
 * @param fileName - Name of the file
 * @param contentType - MIME type of the file
 * @returns React component representing the file icon
 */
export function getFileIcon(
  fileName: string,
  contentType: string
): React.ReactNode {
  if (isDAWProject(fileName)) {
    return <DAWLogo filename={fileName} variant="icon" size="small" />;
  }

  const category = getFileCategory(contentType, fileName);
  const iconProps = { fontSize: 16, sx: { width: 16, height: 16 } };

  switch (category) {
    case 'image':
      return <ImageIcon sx={{ color: 'success.main', ...iconProps.sx }} />;
    case 'video':
      return <VideoIcon sx={{ color: 'info.main', ...iconProps.sx }} />;
    case 'audio':
      return <AudioIcon sx={{ color: 'warning.main', ...iconProps.sx }} />;
    case 'pdf':
      return <PdfIcon sx={{ color: 'error.main', ...iconProps.sx }} />;
    case 'archive':
      return <ArchiveIcon sx={{ color: 'secondary.main', ...iconProps.sx }} />;
    default:
      return <FileIcon sx={{ color: 'text.secondary', ...iconProps.sx }} />;
  }
}

/**
 * Gets a human-readable display string for the file type
 * @param fileName - Name of the file
 * @param contentType - MIME type of the file
 * @returns Display string describing the file type
 */
export function getFileTypeDisplay(
  fileName: string,
  contentType: string
): string {
  if (isDAWProject(fileName)) {
    const dawType = getDAWType(fileName);
    return dawType || 'DAW Project';
  }

  const category = getFileCategory(contentType, fileName);
  // Extract extension only if file has a dot and something after it
  const lastDotIndex = fileName.lastIndexOf('.');
  const hasExtension = lastDotIndex > 0 && lastDotIndex < fileName.length - 1;
  const extension = hasExtension
    ? fileName.substring(lastDotIndex + 1).toUpperCase()
    : '';

  switch (category) {
    case 'image':
      return extension ? `${extension} Image` : 'Image';
    case 'video':
      return extension ? `${extension} Video` : 'Video';
    case 'audio':
      return extension ? `${extension} Audio` : 'Audio';
    case 'pdf':
      return 'PDF Document';
    case 'archive':
      return extension ? `${extension} Archive` : 'Archive';
    default:
      return extension ? `${extension} File` : 'File';
  }
}
